import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { TFunction } from 'i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { convertAreaLabel } from '~/models/area'
import { fromProtoDate } from '~/models/dateTime'
import type { IntradayInversion } from '~/models/intradayBidInversion'
import { convertIntradayBidsFromOrders } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import {
  type IntradayBidRow,
  isMarketOrderRow,
} from '~/pages/IntradayBidding/models/intradayBidRow'
import { detectIntradayDeliveryDateInversions } from '~/pages/IntradayBidding/models/intradayDeliveryDateInversion'
import { intradayGatePhase } from '~/pages/IntradayBidding/models/intradayGateClose'

/** 承認確認モーダルに出す売買逆転 1 件 (同一エリア・同一コマ) の明細。 */
export type IntradayApprovalInversionDetail = {
  areaCode: AreaCode
  timeCode: number
  /** 成行札が絡む交差 (価格を見ずに対向札があれば交差)。 */
  hasMarketOrder: boolean
  /** 指値の買い最高値 (円/kWh、 入札の表記のまま)。 指値の買いが無ければ undefined。 */
  maxBuyPrice: string | undefined
  /** 指値の売り最安値 (円/kWh、 入札の表記のまま)。 指値の売りが無ければ undefined。 */
  minSellPrice: string | undefined
}

/**
 * 承認対象の札が巻き込まれている売買逆転 (AC-BIN-045 の検知結果) を、承認確認モーダルの
 * 明細に直す (AC-BIN-097: 売買逆転チェック結果)。 検知そのものは `detectIntradayBidInversions`
 * の結果を再利用し、ここでは対象札を含むものだけを取り出して価格を添える。
 */
export const inversionDetailsForOrder = (
  orderId: string,
  inversions: readonly IntradayInversion[],
  bids: readonly IntradayBidRow[],
): IntradayApprovalInversionDetail[] =>
  inversions
    .filter((inv) => inv.bidIds.includes(orderId))
    .map((inv) => {
      const members = bids.filter((b) => inv.bidIds.includes(b.id))
      // 価格は入札の表記のまま出す (数値化すると 11.20 が 11.2 になる)。
      const limitPrices = (side: Side): string[] =>
        members
          .filter((b) => b.direction === side && !isMarketOrderRow(b))
          .map((b) => b.priceYenPerKwh)
      const pick = (
        prices: string[],
        better: (a: number, b: number) => boolean,
      ): string | undefined =>
        prices.reduce<string | undefined>(
          (best, p) =>
            best === undefined || better(Number(p), Number(best)) ? p : best,
          undefined,
        )
      return {
        areaCode: inv.areaCode,
        timeCode: inv.timeCode,
        hasMarketOrder: members.some(isMarketOrderRow),
        maxBuyPrice: pick(limitPrices(Side.BUY), (a, b) => a > b),
        minSellPrice: pick(limitPrices(Side.SELL), (a, b) => a < b),
      }
    })

/**
 * 承認確認モーダルの売買逆転の明細 (AC-BIN-097)。 検知対象は承認対象と同じ受渡日の札のうち、
 * 提出しない下書きを除いたもの (AC-BIN-045。 入札追加タブのバナーは一覧に表示中の下書きを含める)。
 * GC を過ぎたかは、 操作口 (承認する / 差戻し、 AC-BIN-099。 backend の `approvalActionable` と
 * 確定直前の判定) と同じく札の `gcAt` で判定する (`gcAt` が無く判定できない札は、 確定直前の
 * 判定と同じく GC 前として扱わない)。
 */
export const intradayApprovalInversionDetails = (
  order: Pick<IntradayOrderRow, 'id' | 'targetDate'>,
  orders: readonly IntradayOrderRow[],
  now: Date,
): IntradayApprovalInversionDetail[] =>
  order.targetDate === undefined
    ? []
    : inversionDetailsForOrder(
        order.id,
        detectIntradayDeliveryDateInversions(
          orders,
          fromProtoDate(order.targetDate),
          {
            isBeforeGc: (o) => intradayGatePhase(o.gcAt, now) === 'open',
            isSubmitTarget: () => false,
          },
        ),
        convertIntradayBidsFromOrders(orders),
      )

/**
 * 売買逆転の警告セクション (承認はブロックしない。 意図的な両建てがありうるため、
 * #7146 論点 3 の決定)。 該当が無ければ undefined。
 */
export const buildIntradayInversionWarningSection = (
  t: TFunction,
  details: readonly IntradayApprovalInversionDetail[],
): ViolationSection | undefined => {
  if (details.length === 0) return undefined
  return {
    title: t('page:biddingIntraday.alert.inversionTitle', {
      count: details.length,
    }),
    details: details.map((d) => {
      const head = t('page:biddingIntraday.approvalAction.inversionHead', {
        timeCode: d.timeCode,
        area: convertAreaLabel(d.areaCode),
      })
      return d.hasMarketOrder ||
        d.maxBuyPrice === undefined ||
        d.minSellPrice === undefined
        ? t('page:biddingIntraday.approvalAction.inversionDetailMarket', {
            head,
          })
        : t('page:biddingIntraday.approvalAction.inversionDetail', {
            head,
            maxBuy: d.maxBuyPrice,
            minSell: d.minSellPrice,
          })
    }),
  }
}
